import { useQuery } from '@tanstack/react-query';
import { type MouseEvent, useMemo } from 'react';

import Spinner from '@/components/Common/Spinner';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import {
  useFeedbackTerminology,
} from '@/entities/workspace/hooks';
import { openSignalFilterFeedbackPreviewDialog } from '@/features/(signal)/signal-issue-detail/ui/signal-feedback-preview-dialog';
import { convertSignalDatePresetToChartTerm } from '@/features/(signal)/signals/lib';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import SearchDrilldownMenu from '@/features/GlobalSearch/components/ResultContents/SearchDrilldownMenu';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services';
import { FeedbackSentiment, sentimentMap } from '@/types/feedbackConst';
import { FEEDBACK_COLUMNS, SENTIMENT_COLUMNS } from '@/types/filterKeyConst';

export const IssueDetailSentimentRatio = (props: {
  issue: CompositedSignalIssue;
  datePreset: SignalDateSelectionInput;
  filterSet: StructuredFilter;
}) => {
  const color = useColor();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const { status, open, close, pos, modalState } =
    useModalState<FeedbackSentiment | null>(null);

  const processedFilter = useMemo(() => {
    const sFilter = new SFilter(props.filterSet);
    sFilter.removeField(FEEDBACK_COLUMNS.DATE_START);
    sFilter.removeField(FEEDBACK_COLUMNS.DATE_END);
    return sFilter.get() || {};
  }, [props.filterSet]);

  const chart: ChartBase & { name: string } = {
    name: 'sentiment-ratio-chart',
    field: {
      type: CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL,
      entire: true,
    },
    graph: CHART_GRAPH.PIE,
    graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
    historical: {
      frequency: null,
      value: false,
    },
    type: CHART_TYPE.SINGLE_AXIS,
    selected_field: {
      method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_10,
      items: [],
    },
    target: {
      filter: processedFilter,
      term: convertSignalDatePresetToChartTerm(props.datePreset),
    },
    series: [
      {
        measure: {
          metric: CHART_METRIC.FEEDBACK,
          calculation: CHART_CALCULATION.COUNT,
        },
      },
    ],
  };

  const { data, isFetching } = useQuery({
    queryKey: ['sentiment-ratio-chart', chart],
    queryFn: () => service.chart.fetchTable(chart),
  });

  const ratios = useMemo(() => {
    if (!data || !data.tables[0]) {
      return { positive: 0, negative: 0, neutral: 0, total: 0 };
    }

    const rows = data.tables[0];
    let positive = 0;
    let negative = 0;
    let neutral = 0;

    rows.forEach((row) => {
      // row.x should be the sentiment enum value
      if ('value' in row) {
        if (Number(row.x) === FeedbackSentiment.POSITIVE) positive = row.value;
        else if (Number(row.x) === FeedbackSentiment.NEGATIVE)
          negative = row.value;
        else if (Number(row.x) === FeedbackSentiment.NEUTRAL)
          neutral = row.value;
      }
    });

    const total = positive + negative + neutral;
    return {
      positive,
      negative,
      neutral,
      total,
    };
  }, [data]);

  const openSentimentPreviewDialog = (
    sentiment: FeedbackSentiment,
    initialOpen?: boolean,
  ) => {
    const sentimentValue = sentimentMap[sentiment].value;
    const sentimentFilter = structuredFilterUtils.buildSingleValue(sentiment);
    const payload = sentimentFilter
      ? (() => {
          const sFilter = new SFilter(props.filterSet);
          sFilter.setValueSet(SENTIMENT_COLUMNS.KEY_SENTIMENT, sentimentFilter);
          return sFilter.get() || props.filterSet;
        })()
      : props.filterSet;

    openSignalFilterFeedbackPreviewDialog({
      title: `${props.issue.name} ${replaceFeedbackTerminology('feedbacks')}`,
      description: `Sentiment: ${sentimentValue}`,
      initialOpen,
      queryKey: [
        'signal',
        'detail',
        props.issue.name,
        'sentiment',
        sentimentValue,
        JSON.stringify(payload),
        initialOpen ? 'expanded' : 'collapsed',
      ],
      payload,
    });
  };

  const onViewFeedbackListHandler = (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    void close();
    if (modalState == null) return;
    openSentimentPreviewDialog(modalState, initialOpen);
  };

  const onBarClick = (sentiment: FeedbackSentiment) => (e: MouseEvent) => {
    e.stopPropagation();
    open({
      modalState: sentiment,
      pos: { top: e.clientY, left: e.clientX },
    });
  };

  if (isFetching) {
    return (
      <div className="flex items-center h-6">
        <Spinner size={16} />
      </div>
    );
  }

  const { positive, negative, neutral, total } = ratios;

  if (total === 0) {
    return <span className="text-sm text-gray-500">-</span>;
  }

  const positivePercent = (positive / total) * 100;
  const negativePercent = (negative / total) * 100;
  const neutralPercent = (neutral / total) * 100;

  // Neutral만 해당 바 세그먼트 중앙에 배치. Positive / Negative는 고정 위치(왼쪽/오른쪽)
  const neutralLabelLeft = positivePercent + neutralPercent / 2;

  return (
    <div className="relative flex flex-col w-full max-w-[300px]">
      <div className="flex w-full h-3 rounded-full overflow-hidden">
        {positive > 0 && (
          <TooltipV2
            tooltipNode={`${sentimentMap[FeedbackSentiment.POSITIVE].value}: ${positive} (${positivePercent.toFixed(1)}%)`}
          >
            <div
              className="cursor-pointer"
              style={{
                width: `${positivePercent}%`,
                backgroundColor: color.sentiment[FeedbackSentiment.POSITIVE],
              }}
              onClick={onBarClick(FeedbackSentiment.POSITIVE)}
            />
          </TooltipV2>
        )}
        {neutral > 0 && (
          <TooltipV2
            tooltipNode={`${sentimentMap[FeedbackSentiment.NEUTRAL].value}: ${neutral} (${neutralPercent.toFixed(1)}%)`}
          >
            <div
              className="cursor-pointer"
              style={{
                width: `${neutralPercent}%`,
                backgroundColor: color.sentiment[FeedbackSentiment.NEUTRAL],
              }}
              onClick={onBarClick(FeedbackSentiment.NEUTRAL)}
            />
          </TooltipV2>
        )}
        {negative > 0 && (
          <TooltipV2
            tooltipNode={`${sentimentMap[FeedbackSentiment.NEGATIVE].value}: ${negative} (${negativePercent.toFixed(1)}%)`}
          >
            <div
              className="cursor-pointer"
              style={{
                width: `${negativePercent}%`,
                backgroundColor: color.sentiment[FeedbackSentiment.NEGATIVE],
              }}
              onClick={onBarClick(FeedbackSentiment.NEGATIVE)}
            />
          </TooltipV2>
        )}
      </div>
      <div className="absolute top-4 left-0 right-0 w-full text-xs text-muted-foreground pointer-events-none">
        {positive > 0 && (
          <span className="absolute left-0">
            {Math.round(positivePercent)}%
          </span>
        )}
        {neutral > 0 && (
          <span
            className="absolute -translate-x-1/2"
            style={{ left: `${neutralLabelLeft}%` }}
          >
            {Math.round(neutralPercent)}%
          </span>
        )}
        {negative > 0 && (
          <span className="absolute right-0">
            {Math.round(negativePercent)}%
          </span>
        )}
      </div>
      <SearchDrilldownMenu
        pos={{
          top: typeof pos.top === 'number' ? pos.top : 0,
          left: typeof pos.left === 'number' ? pos.left : 0,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={(e) => onViewFeedbackListHandler(e)}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </div>
  );
};
